/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2020,2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

/**
 * @class VisDnDUtils
 * @hideconstructor
 *
 * @classdesc Utility feature for drag and drop of metadata onto a visualization content
 */
define([
	'underscore',
	'../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'./api/VisDnDUtilsAPI',
	'./OlapHelper.v2',
	'./LocalFilterMemberDNDHelper',
	'./VisMapColumnsToSlot',
	'dashboard-analytics/dataSources/utils/ShapingUIUtils',
	'./api/impl/VisDnDColumnToSlotProvider',
	'./api/impl/VisDnDSlotToSlotProvider',
	'./api/impl/VisDnDShapeToSlotProvider',
	'./api/impl/VisDnDSlotLocalFilterProvider',
	'./api/impl/VisDndLiveWidgetProvider',
	'dashboard-analytics/dataSources/utils/ShapingConstants',
	'dashboard-analytics/apiHelpers/SlotAPIHelper'

], (
	_,
	APIFactory,
	VisDnDUtilsAPI,
	OlapHelperV2,
	LocalFilterMemberDNDHelper,
	VisMapColumnsToSlot,
	ShapingUIUtils,
	VisDnDColumnToSlotProvider,
	VisDnDSlotToSlotProvider,
	VisDnDShapeToSlotProvider,
	VisDnDSlotLocalFilterProvider,
	VisDndLiveWidgetProvider,
	ShapingConstants,
	SlotAPIHelper
) => {

	const METADATA_TYPES = [
		'MODEL_ITEM',
		'GRID_HEADER_ITEM',
		'filter' // Used for FM package model filters ?
	];

	class VisDnDUtils {
		constructor({ features, content }) {
			this.content = content;
			this.dashboard = features['API'];
			this.dashboardDnD = features['Dashboard.DashboardDnd'];
			this.transaction = features['Dashboard.Transaction'];
			this.datasources = features['Dashboard.DataSources'];
			this.datasourcesMoser = features['Dashboard.DataSources.moser'];
			this.state = features['state'];
			this.visualization = features['Visualization'];
			this.visRecommender = features['Visualization.SmartsRecommender'];
			this.visDnD = features['VisDnD'];

			this.olapHelper = new OlapHelperV2(this.visualization);
			this.visMapColumnsToSlot = new VisMapColumnsToSlot({
				content: this.content,
				visualization: this.visualization,
				transaction: this.transaction
			});

			this._initializeProviders();
		}

		getAPI () {
			if (!this.api) {
				this.api = APIFactory.createAPI(this, [VisDnDUtilsAPI]);
			}
			return this.api;
		}

		_initializeProviders () {
			const param = {
				visDnDUtils: this,
				dashboardAPI: this.dashboard,
				dashboardDnD: this.dashboardDnD,
				datasources: this.datasources,
				datasourcesMoser: this.datasourcesMoser,
				transaction: this.transaction,
				content: this.content,
				state: this.state,
				visualization: this.visualization,
				visRecommender: this.visRecommender
			};

			// Register the all livewidget providers
			const shapeToSlotProvider = new VisDnDShapeToSlotProvider(param);
			this.visDnD.registerProviders('widget.live', [ new VisDndLiveWidgetProvider(param), shapeToSlotProvider ],['slot.item']);

			// Register the all slot.item providers
			const slotItemProviders = [
				VisDnDColumnToSlotProvider,
				VisDnDSlotToSlotProvider
			].map((ProviderClass) => {
				return new ProviderClass(param);
			});
			this.visDnD.registerProviders('slot.item', [...slotItemProviders, shapeToSlotProvider]);

			// Register the slot.filter provider
			this.visDnD.registerProviders('slot.filter', [ new VisDnDSlotLocalFilterProvider(param) ]);
		}

		destroy () {
			this.dashboard = null;
			this.content = null;
			this.transaction = null;
			this.visualization = null;

			this.olapHelper = null;
			this.visMapColumnsToSlot = null;
		}

		_isSameDataSource (dragData) {
			const sourceId = dragData.sourceId;
			const dataSource = this.visualization.getDataSource();
			const currentModuleId = dataSource ? dataSource.getId() : null;
			return (currentModuleId && sourceId !== currentModuleId) ? false : true;
		}

		/**
		 * return all the projected dataItems except MultiMeasure item.
		 */
		_getProjectedDataItemList () {
			if (!this.visualization) {
				throw new Error('Invalid visualization');
			}

			let dataItemList = this.visualization.getSlots().getDataItemList();
			dataItemList = dataItemList.filter(dataItem=>dataItem.getColumnId() !== SlotAPIHelper.MULTI_MEASURES_SERIES);
			return dataItemList.map(dataItem => dataItem.getMetadataColumn());
		}

		_acceptsOlapColumnsV2(payload, { dropTarget, dropAction }) {
			const metadataColumns = _.map(payload.data.columns, (column) => column.metadataColumn);
			if (dropTarget === ShapingConstants.DROP_TARGET_OPTIONS.FILTER
				|| !metadataColumns.some(col => col.isOlapColumn())) {
				return true;
			}

			let targetSlotId;
			let position, bReplace;
			if (dropAction) {
				//When dnd hover on crosstab, GridDnDManager composes dropAction and call VisDnD.accepts()
				position = dropAction.actionSpec.position;
				bReplace =  dropAction.actionSpec.replace;
				const targetSlotAPI = dropAction.actionSpec.slot;
				targetSlotId = targetSlotAPI.getDefinition().getId();
			} else {
				return true;
			}

			return this.acceptsOlapV2({
				targetSlotId: targetSlotId,
				sourceSlotId: undefined,
				indexInSourceSlot: undefined,
				sourceColumns: metadataColumns,
				indexInTargetSlot: position,
				isReplace: bReplace
			});
		}

		accepts (payload = {}, options = {}) {
			//No visualizationAPI return false
			//No metadata provided return false
			if (!this.visualization || !payload.data || !this._isSameDataSource(payload.data)) {
				return false;
			}

			// No restrictions need to be applied when drop target is filter.
			const checkHierarchy = !options || options.dropTarget !== ShapingConstants.DROP_TARGET_OPTIONS.FILTER;
			if (checkHierarchy) {
				/**
				 * Map of hierarchies used to check if the dragObject follows the drop rules.
				 */
				const oHierarchyMap = {};
				const metadataColumns = _.map(payload.data.columns, (columns) => columns.metadataColumn);
				const metadataColumnsWithMembers = payload.data.utils && payload.data.utils.getColumnsWithMembers();

				// Check the metadata item(s) in dragObject
				if (!metadataColumns) {
					return false;
				}

				if (ShapingUIUtils.isInvalidDragObject(metadataColumns, oHierarchyMap, metadataColumnsWithMembers)) {
					return false;
				}

				// Check dragObject with widget metadata
				// Members from the same hierarchy can only be dropped to same slot.
				const projectedMetadataColumns = this._getProjectedDataItemList();
				if (options.dropTarget === ShapingConstants.DROP_TARGET_OPTIONS.SLOT) {
					let slotMetadataColumnIds = [];
					if (options.targetNode) {
						const slotId = options.targetNode.getAttribute('data-slot-id');
						const slotDataItemList = slotId && this.visualization.getSlots().getSlot(slotId).getDataItemList();
						slotMetadataColumnIds = _.map(slotDataItemList, dataItem => dataItem.getColumnId() !== SlotAPIHelper.MULTI_MEASURES_SERIES).filter(Boolean);
					}

					// Get restricted columns that are not in the target slot
					const columnsNotInTargetSlot = slotMetadataColumnIds.length ? _.filter(projectedMetadataColumns, column => slotMetadataColumnIds.indexOf(column.getId()) === -1) : projectedMetadataColumns;
					// Restrict any metadata object from the same hierarchy to be dropped to a different slot
					const dragColumnProjected = _.some(columnsNotInTargetSlot, column =>
						_.some(metadataColumns, dragColumn => column.getId() === dragColumn.getId() &&
						(column.isHierarchy() || column.isNamedSet())));
					if (dragColumnProjected) {
						return false;
					}
				}

				if (ShapingUIUtils.isInvalidDragObject(projectedMetadataColumns, oHierarchyMap, null, /** metadataColumnsExist */ true)) {
					return false;
				}
			}
			if (this.visualization.getType() === 'Crosstab') {
				if (!this._acceptsOlapColumnsV2(payload, options)) {
					return false;
				}
			}
			return payload.data && payload.data.utils && payload.data.utils.isValid(options) && METADATA_TYPES.includes(payload.type) ? true : false;
		}

		onDrop (payload) {
			return (METADATA_TYPES.includes(payload.type)) ? payload.data: null;
		}

		mapColumns(slotId, droppedColumns, options, transactionToken) {
			return this.visMapColumnsToSlot.mapColumns(slotId, droppedColumns, options, transactionToken);
		}

		acceptsOlapV2 (params) {
			return this.olapHelper.acceptsOlapV2(params);
		}

		addMembersAsLocalFilters (options = {}, transactionToken) {
			const dataItemList = options.slotAPI ? options.slotAPI.getDataItemList() : this.visualization.getSlots().getDataItemList();
			const columns = options.columns ? options.columns.slice() : [];
			let removedExistingDataItem = options.columns || [];
			const _removeResultFn = function (index) {
				if (index > -1 && index < columns.length) {
					removedExistingDataItem.splice(index, 1);
				}
			}.bind(this);

			const localFiltersSpec = [];
			columns.forEach((column) => {
				const newLocalFilters = column.original.members;

				if (newLocalFilters && newLocalFilters.length) {
					const hasMappedDatItem = LocalFilterMemberDNDHelper.hasDataItem(dataItemList, column.metadataColumn.getId());
					const itemContext = LocalFilterMemberDNDHelper.getItemContext(dataItemList, column.original.metadataColumn);
					LocalFilterMemberDNDHelper.addOrUpdateFilter({
						bReplace: options.bReplace,
						itemContext: itemContext,
						localFilters: this.visualization.getLocalFilters(),
						newLocalFilters: newLocalFilters,
						localFiltersSpec: localFiltersSpec,
						doUpdateFilters: options.doUpdateFilters
					}, transactionToken);

					const appendMembersToExistingDataItem = hasMappedDatItem && !options.bReplace;

					if (appendMembersToExistingDataItem) {
						removedExistingDataItem.forEach((col, index) => {
							if (column.metadataColumn.getId() === col.metadataColumn.getId()) {
								_removeResultFn(index);
								return;
							}
						});
					}
				}
			});
			return localFiltersSpec;
		}

		exceedsItemsLimit(countOfItemsToDrop, indexInSlot, addAfter, slot) {
			const limit = slot.getDefinition().getMaxItems();
			if (typeof limit !== 'number' || limit < 0) {
				// no limit verification if maxItems is null or undefined or something else than a positive number (0 is considered as positive in this function)
				return false;
			}
			const mappedItems = slot.getDataItemList().length;
			//When dropping on top of an existing slot dataItem, we replace it with the items we're dropping.
			//EXCEPTION....for index===-1, we always add to the existing set of items.
			//indexInSlot===-1 means add at the end.
			//indexInSlot===-1 and addAfter = true means add at the beginning.
			const mappedItemsAfterDrop = countOfItemsToDrop + mappedItems - ((addAfter || indexInSlot===-1) ? 0 : 1);
			return mappedItemsAfterDrop > limit;
		}

		validateSupportsOLAP(metadataColumns) {
			let supported = true;
			const visDefinition = this.visualization.getDefinition();
			if (visDefinition.getProperty('supportsOLAP') === false) {
				metadataColumns && metadataColumns.forEach(metadataColumn=>{
					if (metadataColumn.isOlapColumn() || metadataColumn.isNamedSet()) {
						supported = false;
					}
				});
			}
			return supported;
		}
	}

	return VisDnDUtils;
});
